import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/authContext.tsx";
import { getPagePath, isPageRegistered } from "../../pages/pageRegistry.ts";
import { StatusIcon } from "../ui/statusIcon.tsx";
import { AssistantAvatar } from "../ui/assistantAvatar.tsx";
import { useAssistant } from "../../config/assistantProfile.ts";
import { IssaQuickChat } from "../../pages/issaInsights/issaQuickChat.tsx";
import type { WebAdminPage } from "../../types/session.ts";
import styles from "./commandPalette.module.scss";

interface PaletteEntry {
  page: WebAdminPage;
  score: number;
}

function isPageAvailableForModule(page: WebAdminPage, moduleId: number): boolean {
  if (!page.Modules) return true;
  return page.Modules.split(",").map(Number).includes(moduleId);
}

function scorePage(page: WebAdminPage, query: string): number {
  if (!query) return 1;
  const name = (page.DisplayName ?? "").toLowerCase();
  const q = query.toLowerCase();
  if (name === q) return 100;
  if (name.startsWith(q)) return 80;
  const wordStart = name.split(/[\s\-/]+/).some((w) => w.startsWith(q));
  if (wordStart) return 60;
  if (name.includes(q)) return 40;
  let i = 0;
  for (const c of name) {
    if (c === q[i]) i++;
    if (i === q.length) return 20;
  }
  return 0;
}

// Ask-Issa mode triggers: leading "?" or natural-language "how do i …" prefix.
function isAskMode(query: string): boolean {
  const q = query.trimStart();
  if (q.startsWith("?")) return true;
  return /^how (do|can|should) i\b/i.test(q);
}

function askPrompt(query: string): string {
  const q = query.trimStart();
  return q.startsWith("?") ? q.slice(1).trim() : q.trim();
}

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { cache, canRead, activeEndpoint } = useAuth();
  const navigate = useNavigate();
  const assistant = useAssistant();
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const [chatPrompt, setChatPrompt] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const moduleId = activeEndpoint?.ModuleID ?? 0;
  const askMode = isAskMode(query);

  const entries = useMemo((): PaletteEntry[] => {
    if (askMode) return [];
    const pages = cache?.PageInfo?.Page ?? [];
    const out: PaletteEntry[] = [];
    for (const page of pages) {
      if (!page.Enabled || !page.Available) continue;
      if (page.Type === "IMP") continue;
      // SUB pages are normally menu containers, not destinations — but if the
      // page name has a registry entry (custom React page registered under the
      // SUB name), it IS reachable, so let it through.
      if (page.Type === "SUB" && !isPageRegistered(page.Name)) continue;
      if (!canRead(page.RowID)) continue;
      if (!isPageAvailableForModule(page, moduleId)) continue;
      const score = scorePage(page, query.trim());
      if (score > 0) out.push({ page, score });
    }
    out.sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return (a.page.DisplayName ?? "").localeCompare(b.page.DisplayName ?? "");
    });
    return out.slice(0, 50);
  }, [askMode, cache, canRead, moduleId, query]);

  useEffect(() => {
    if (!open) return;
    setQuery("");
    setActiveIdx(0);
    setChatPrompt(null);
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [open]);

  useEffect(() => {
    setActiveIdx(0);
  }, [query]);

  useEffect(() => {
    if (!open) return;
    const active = listRef.current?.querySelector<HTMLElement>(`[data-idx="${activeIdx}"]`);
    active?.scrollIntoView({ block: "nearest" });
  }, [open, activeIdx]);

  if (!open) return null;

  if (chatPrompt) {
    return <IssaQuickChat open prompt={chatPrompt} onClose={onClose} />;
  }

  function submitAsk() {
    const prompt = askPrompt(query);
    if (!prompt) return;
    setChatPrompt(prompt);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, entries.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      if (askMode) {
        e.preventDefault();
        submitAsk();
        return;
      }
      const entry = entries[activeIdx];
      if (entry) {
        e.preventDefault();
        navigate(getPagePath(entry.page.Name));
        onClose();
      }
    }
  }

  return (
    <div className={styles.backdrop} onClick={onClose} role="presentation">
      <div
        className={styles.panel}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Command palette"
        aria-modal="true"
      >
        <div className={styles.inputRow}>
          <span className={styles.inputIcon}>
            {askMode ? <AssistantAvatar size={16} /> : <StatusIcon kind="search" size={16} />}
          </span>
          <input
            ref={inputRef}
            type="text"
            className={styles.input}
            placeholder={askMode ? `Ask ${assistant.name}…` : `Search pages, or type ? to ask ${assistant.name}`}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            aria-label={askMode ? `Ask ${assistant.name}` : "Search pages"}
            aria-controls="palette-results"
            aria-activedescendant={!askMode && entries[activeIdx] ? `palette-item-${activeIdx}` : undefined}
          />
          {!askMode && <span className={styles.count}>{entries.length}</span>}
        </div>
        <div ref={listRef} className={styles.results} id="palette-results" role="listbox">
          {askMode ? (
            <div
              className={`${styles.resultItem} ${styles.resultItemActive}`}
              role="option"
              aria-selected="true"
              onClick={submitAsk}
            >
              <span className={styles.resultName}>
                Ask {assistant.name}: {askPrompt(query) || <span className={styles.resultHint}>type your question…</span>}
              </span>
              <span className={styles.resultHint}>Enter to send</span>
            </div>
          ) : entries.length === 0 ? (
            <div className={styles.empty}>No pages match "{query}"</div>
          ) : (
            entries.map((entry, i) => (
              <div
                key={entry.page.RowID}
                id={`palette-item-${i}`}
                data-idx={i}
                role="option"
                aria-selected={i === activeIdx}
                className={`${styles.resultItem}${i === activeIdx ? ` ${styles.resultItemActive}` : ""}`}
                onMouseEnter={() => setActiveIdx(i)}
                onClick={() => {
                  navigate(getPagePath(entry.page.Name));
                  onClose();
                }}
              >
                <span className={styles.resultName}>{entry.page.DisplayName}</span>
                <span className={styles.resultHint}>{entry.page.Name}</span>
              </div>
            ))
          )}
        </div>
        <div className={styles.footer}>
          <span className={styles.footerGroup}>
            <kbd className={styles.kbd}>↑↓</kbd> navigate
          </span>
          <span className={styles.footerGroup}>
            <kbd className={styles.kbd}>Enter</kbd> open
          </span>
          <span className={styles.footerGroup}>
            <kbd className={styles.kbd}>Esc</kbd> close
          </span>
        </div>
      </div>
    </div>
  );
}
